import {
    GuxFormFieldCheckbox,
    GuxDropdownMulti,
    GuxListboxMulti,
    GuxOptionMulti,
    GuxFormFieldDropdown,
} from "genesys-spark-components-react";
import strings from "./CalibrationSettings.strings";
import { useStrings } from "features/i18n/useStrings";
import {
    fetchUsersMultiSelect,
    selectListedUsers,
    usersSliceActions,
} from "features/users/usersSlice";
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import embeddingService from "services/embeddingService";
import { User } from "@genesys/cloud-frontend/dist/mjs/models";
import {
    scorableSliceActions,
    selectIsConversationAgeLimitReached,
} from "features/scorable/scorableSlice";
import { DefaultLimits } from "constants/DefaultLimits";
import { selectCalibrationEvaluators } from "features/scorable/ScorableFormValidations";
import { selectUserById } from "features/users/usersSlice";
import "./CalibrationSettings.less";
import { debounce } from "lodash";

export type CalibrationError = {
    errorType: string;
    userId?: string;
};

export function CalibrationSettings({
    calibrationMode,
    toggleCalibrationMode,
    recordCalibrationCreationError,
    setRecordCalibrationCreationError,
}: {
    calibrationMode: boolean;
    toggleCalibrationMode: () => void;
    recordCalibrationCreationError: CalibrationError;
    setRecordCalibrationCreationError: React.Dispatch<
        React.SetStateAction<CalibrationError>
    >;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();

    const noCalibrationEvaluators = !useAppSelector(
        selectCalibrationEvaluators
    );
    const hasScoringExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.scoringExpiry
        );
    });
    const isPermissionsError =
        recordCalibrationCreationError.errorType === "PermissionsError";
    const name = useAppSelector((state) =>
        selectUserById(state, recordCalibrationCreationError.userId ?? "")
    );
    const listedUsers = useAppSelector(selectListedUsers);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((newTerm) => {
            dispatch(fetchUsersMultiSelect(newTerm)).then((results) => {
                if ("error" in results) {
                    embeddingService.notifyErrors("userLoading-failed");
                }
            });
        }, 300),
        []
    );

    return (
        <div className="CalibrationSettings">
            {!hasScoringExpired && (
                <>
                    <GuxFormFieldCheckbox>
                        <input
                            slot="input"
                            type="checkbox"
                            checked={calibrationMode}
                            onChange={() => {
                                if (calibrationMode) {
                                    dispatch(usersSliceActions.clearUsers());
                                    dispatch(
                                        scorableSliceActions.clearCalibration()
                                    );
                                }
                                toggleCalibrationMode();
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    if (calibrationMode) {
                                        dispatch(
                                            scorableSliceActions.clearCalibration()
                                        );
                                        dispatch(
                                            usersSliceActions.clearUsers()
                                        );
                                    }
                                    toggleCalibrationMode();
                                }
                            }}
                            className="Clickable"
                        />
                        <label slot="label" className="Clickable">
                            {t.labels.calibrationCheckBox}
                        </label>
                    </GuxFormFieldCheckbox>
                    {calibrationMode && (
                        <GuxFormFieldDropdown labelPosition="screenreader">
                            {noCalibrationEvaluators ? (
                                <span slot="error" role="alert">
                                    {t.errors.noSelectedUsers}
                                </span>
                            ) : (
                                <>
                                    {isPermissionsError && (
                                        <span slot="error" role="alert">
                                            {t.errors.evaluatorsCantScore({
                                                userName: name,
                                            })}
                                        </span>
                                    )}
                                </>
                            )}
                            <label slot="label">
                                {t.labels.qualityEvaluatorSelection}
                            </label>
                            <GuxDropdownMulti
                                data-testid="quality-evaluator-box"
                                placeholder={t.labels.evaluatorPlaceholderText}
                                filterType="custom"
                                onGuxfilter={(event) => {
                                    debouncedSearch(event.detail);
                                }}
                                onGuxcollapsed={() => {
                                    debouncedSearch("");
                                }}
                                onInput={(e) => {
                                    const selectedValue = (
                                        e.target as HTMLInputElement
                                    ).value;
                                    const selectedTopics =
                                        selectedValue === undefined
                                            ? undefined
                                            : selectedValue
                                                  .split(",")
                                                  .map((id) => {
                                                      return { id } as User;
                                                  });
                                    dispatch(
                                        scorableSliceActions.setCalibrationEvaluators(
                                            selectedTopics
                                        )
                                    );
                                    setRecordCalibrationCreationError({
                                        errorType: "",
                                    });
                                }}
                            >
                                <GuxListboxMulti aria-label={t.labels.users}>
                                    {Object.values(listedUsers).map((user) => {
                                        return (
                                            <GuxOptionMulti
                                                data-testid="calibration-evaluator-option"
                                                value={user.id}
                                                key={user.id}
                                            >
                                                {user.name}
                                            </GuxOptionMulti>
                                        );
                                    })}
                                </GuxListboxMulti>
                            </GuxDropdownMulti>
                        </GuxFormFieldDropdown>
                    )}
                </>
            )}
        </div>
    );
}
